import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import {
	SELF_EXCLUSION_WARNING_DIALOG_ID,
	selfExclusionWarningDialogFactory,
} from "@obg/mat.ui.common.responsible-gaming";
import { AlertButtonColor, DialogComponentData, DialogService } from "@obg/mat.ui.core";
import { CommonTrackingContext } from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	JurisdictionPrefixService,
	ResponsibleGamingAction,
	SelfExclusionRequest,
	Timeout,
} from "@obg/ngx.core";
import { CoreContentState, TranslationGroup, TranslationSelector } from "@obg/ngx.core.content";
import { TelemetryService } from "@obg/ngx.global.evo";
import { combineLatest, Observable, of, Subscription } from "rxjs";
import { switchMap, take } from "rxjs/operators";

import {
	LoginStatus,
	SELF_EXCLUSION,
	SELF_EXCLUSION_ALERT_ID,
	SelfExclusionType,
	TRANSLATION_KEYS,
} from "./self-exclusion.const";

@Injectable({
	providedIn: "root",
})
export class SelfExclusionService {
	private readonly USE_KEYS_WITHOUT_PREFIX = ["ksa", "coga", "cgl"];

	constructor(
		private responsibleGamingAction: ResponsibleGamingAction,
		private telemetryService: TelemetryService,
		private translationSelector: TranslationSelector,
		private alertService: AbstractTranslatableAlertService,
		private jurisdictionPrefixService: JurisdictionPrefixService,
		private store: Store<CoreContentState>,
		private dialogService: DialogService
	) {}

	confirmSelfExclusion(
		{ numberOfDays, text }: Timeout,
		isFromAllBrands = false,
		isCentrallySelfExcluded = false
	): Subscription {
		return combineLatest([
			this.store.pipe(this.translationSelector.getByKey$(text)),
			this.getKey$(TRANSLATION_KEYS.confirm_title),
			this.getKey$(TRANSLATION_KEYS.confirm_message),
		]).subscribe(([period, title, message]) => {
			this.alertService.open(
				{
					id: SELF_EXCLUSION_ALERT_ID,
					title,
					message,
					buttons: [
						{
							text: TRANSLATION_KEYS.cancel_button,
							color: AlertButtonColor.Primary,
						},
						{
							text: TRANSLATION_KEYS.confirm_button,
							handler: () =>
								this.handleSelfExclusion({
									days: numberOfDays,
									isFromAllBrands,
									isCentrallySelfExcluded,
								}),
						},
					],
					dialogConfig: {
						disableClose: true,
					},
					hideClose: true,
					mustBeInteracted: true,
				},
				[TranslationGroup.Common],
				{ period }
			);
		});
	}

	private getKey$(translationKey: string): Observable<string> {
		return this.jurisdictionPrefixService.getJurisdictionPrefix$().pipe(
			take(1),
			switchMap((prefix) =>
				this.USE_KEYS_WITHOUT_PREFIX.includes(prefix)
					? of(translationKey)
					: this.jurisdictionPrefixService.addJurisdictionPrefix$(translationKey)
			)
		);
	}

	handleSelfExclusion(payload: SelfExclusionRequest) {
		this.store.dispatch(this.responsibleGamingAction.updateSelfExclusionPeriod(payload));
		this.telemetryService.track({
			event: CommonTrackingContext.VirtualPageView,
			LoginStatus: LoginStatus.LoggedIn,
			VirtualTitle: SELF_EXCLUSION,
		});
	}

	showSelfExclusionWarning(
		selfExclusionPeriod: Timeout,
		centrallyExcluded: boolean,
		selfExclusionType: SelfExclusionType | undefined
	) {
		const data: DialogComponentData = {
			hideClose: false,
			hideFooter: true,
		};

		this.dialogService.openComponent(
			selfExclusionWarningDialogFactory(selfExclusionPeriod, centrallyExcluded, selfExclusionType),
			{
				id: SELF_EXCLUSION_WARNING_DIALOG_ID,
				disableClose: false,
				data,
			}
		);
	}
}
